import { useMemo, useState } from 'react';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';

const VIEWS = [
    { id: 'sessions', label: 'Every session' },
    { id: 'labs', label: 'Lab summary' },
    { id: 'grid', label: 'Score grid' }
];

const tone = (score) => (score === null || score === undefined ? 'text-muted-foreground'
    : score >= 70 ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
        : score >= 50 ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'bg-red-500/10 text-red-700 dark:text-red-300');

const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-');
const fmt = (v) => (v === null || v === undefined ? '-' : Math.round(v));

const ScorePill = ({ score, testId }) => (
    <span className={cn('inline-flex min-w-[2.5rem] justify-center rounded-md px-1.5 py-0.5 text-sm font-semibold tabular-nums', tone(score))} data-testid={testId}>{fmt(score)}</span>
);

const Tiles = ({ summary }) => (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-5" data-testid="roleplay-report-summary">
        {[
            ['Sessions', summary.total_sessions, 'roleplay-summary-sessions'],
            ['People', summary.unique_users, 'roleplay-summary-users'],
            ['Avg score', summary.average_score, 'roleplay-summary-avg'],
            ['Labs practised', `${summary.labs_practiced}/${summary.total_labs}`, 'roleplay-summary-labs'],
            ['Scored 70+', `${summary.pass_rate}%`, 'roleplay-summary-pass-rate']
        ].map(([label, value, testId]) => (
            <div key={label} className="rounded-lg border bg-rose-500/5 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
                <p className="text-xl font-semibold tabular-nums" data-testid={testId}>{value}</p>
            </div>
        ))}
    </div>
);

export const SessionsTable = ({ rows }) => (
    <Table data-testid="roleplay-sessions-table">
        <TableHeader><TableRow>
            {['User', 'Department', 'Lab', 'Difficulty', 'Overall', 'Framework', 'Skill scores', 'Completed'].map((h) => <TableHead key={h}>{h}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>
            {rows.map((r, i) => (
                <TableRow key={r.session_id} data-testid={`roleplay-session-row-${i}`}>
                    <TableCell><p className="font-medium">{r.user_name || '-'}</p><p className="text-xs text-muted-foreground">{r.email}</p></TableCell>
                    <TableCell>{r.department || '-'}</TableCell>
                    <TableCell><p className="font-medium">{r.lab_name}</p><p className="text-xs text-muted-foreground">{r.category_name}</p></TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{r.difficulty || '-'}</Badge></TableCell>
                    <TableCell><ScorePill score={r.overall_score} testId={`roleplay-session-score-${i}`} /></TableCell>
                    <TableCell><ScorePill score={r.framework_score} /></TableCell>
                    <TableCell className="max-w-[280px]">
                        <div className="flex flex-wrap gap-1">
                            {r.skill_scores.length ? r.skill_scores.map((s) => (
                                <span key={s.key} className={cn('rounded px-1.5 py-0.5 text-[11px]', tone(s.score))}>{s.label} {fmt(s.score)}</span>
                            )) : <span className="text-xs text-muted-foreground">-</span>}
                        </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{day(r.completed_at)}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const LabsTable = ({ labs }) => (
    <Table data-testid="roleplay-labs-table">
        <TableHeader><TableRow>
            {['Lab', 'Category', 'Difficulty', 'Sessions', 'People', 'Avg', 'Best', 'Lowest', 'Last completed'].map((h) => <TableHead key={h}>{h}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>
            {labs.map((l) => (
                <TableRow key={l.scenario_id} className={l.sessions ? '' : 'opacity-60'} data-testid={`roleplay-lab-row-${l.scenario_id}`}>
                    <TableCell className="font-medium">{l.lab_name}</TableCell>
                    <TableCell>{l.category_name}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{l.difficulty || '-'}</Badge></TableCell>
                    <TableCell className="tabular-nums">{l.sessions}</TableCell>
                    <TableCell className="tabular-nums">{l.users}</TableCell>
                    <TableCell><ScorePill score={l.average_score} testId={`roleplay-lab-avg-${l.scenario_id}`} /></TableCell>
                    <TableCell><ScorePill score={l.best_score} /></TableCell>
                    <TableCell><ScorePill score={l.lowest_score} /></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{day(l.last_completed_at)}</TableCell>
                </TableRow>
            ))}
        </TableBody>
    </Table>
);

const ScoreGrid = ({ grid }) => {
    const [onlyActive, setOnlyActive] = useState(false);
    const rows = useMemo(() => (onlyActive ? grid.rows.filter((r) => r.labs_tried > 0) : grid.rows), [grid.rows, onlyActive]);
    return (
        <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={onlyActive} onCheckedChange={setOnlyActive} data-testid="roleplay-grid-only-active" />
                Only people who have practised
            </label>
            <p className="text-xs text-muted-foreground">Best score per lab. A dash means the person hasn't tried that lab yet.</p>
            <Table data-testid="roleplay-grid-table">
                <TableHeader><TableRow>
                    <TableHead className="sticky left-0 z-10 min-w-[180px] bg-card">Person</TableHead>
                    <TableHead>Labs tried</TableHead>
                    <TableHead>Avg best</TableHead>
                    {grid.labs.map((l) => (
                        <TableHead key={l.scenario_id} className="min-w-[92px] whitespace-normal text-[11px] leading-tight" title={`${l.lab_name} (${l.category_name})`}>{l.lab_name}</TableHead>
                    ))}
                </TableRow></TableHeader>
                <TableBody>
                    {rows.map((r, i) => (
                        <TableRow key={r.user_id} data-testid={`roleplay-grid-row-${i}`}>
                            <TableCell className="sticky left-0 z-10 bg-card"><p className="font-medium">{r.user_name || '-'}</p><p className="text-xs text-muted-foreground">{r.department || r.email}</p></TableCell>
                            <TableCell className="tabular-nums">{r.labs_tried}/{grid.labs.length}</TableCell>
                            <TableCell><ScorePill score={r.average_best} /></TableCell>
                            {grid.labs.map((l) => {
                                const cell = r.scores[l.scenario_id];
                                return (
                                    <TableCell key={l.scenario_id} className="text-center" title={cell ? `${cell.attempts} attempt(s), latest ${fmt(cell.latest)}` : 'Not tried yet'}>
                                        {cell ? <ScorePill score={cell.best} /> : <span className="text-muted-foreground">-</span>}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
};

export const RolePlayLabReport = ({ data, view, onViewChange }) => (
    <div className="space-y-5" data-testid="roleplay-lab-report">
        <Tiles summary={data.summary} />
        <div className="inline-flex rounded-lg border p-1" role="tablist">
            {VIEWS.map((v) => (
                <button key={v.id} type="button" role="tab" aria-selected={view === v.id} onClick={() => onViewChange(v.id)}
                    className={cn('rounded-md px-4 py-1.5 text-sm font-medium transition-colors', view === v.id ? 'bg-rose-600 text-white' : 'text-muted-foreground hover:bg-muted')}
                    data-testid={`roleplay-view-${v.id}`}>
                    {v.label}
                </button>
            ))}
        </div>
        <div className="overflow-x-auto">
            {view === 'sessions' && (data.data.length ? <SessionsTable rows={data.data} /> : <p className="py-10 text-center text-sm text-muted-foreground" data-testid="roleplay-no-sessions">No Role-Play Lab sessions in this time range. Try a longer time range.</p>)}
            {view === 'labs' && <LabsTable labs={data.labs} />}
            {view === 'grid' && <ScoreGrid grid={data.grid} />}
        </div>
    </div>
);
